
import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";

import {
  Box,
  Button,
  TextField,
  Typography,
} from "@mui/material";

import { Table } from "../../layout";
import * as actions from "../../api/actions/pos";
import { AppContext } from "../../App";
import { controller, endpoints } from "../../api/actions/api";
import { safeOnComplete } from "../../utils/safeOnComplete";
import { formatCurrency } from "../../hooks/Currency";
import ReportPrintButton from "./ReportPrintButton";

const ProductReport = (props) => {
  const { t } = useTranslation();
  const { _store, _getById } = props;
  const { setLoader } = useContext(AppContext);

  const reportRef = useRef(null);

  // Original sales from API
  const [sales, setSales] = useState([]);

  // Period filters
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const invalidRange = Boolean(
    fromDate && toDate && fromDate > toDate
  );

  // Load sales when store changes
  useEffect(() => {
    setSales([]);

    if (!_store?.id) {
      return;
    }

    setLoader(true);

    const onComplete = safeOnComplete(
      (res) => {
        setSales(
          Array.isArray(res.result)
            ? res.result
            : []
        );

        setLoader(false);
      },
      (err) => {
        console.error("Failed to load sales:", err);
        setSales([]);
        setLoader(false);
      }
    );

    _getById(
      endpoints.Sell + controller.GetSellsByStore,
      _store.id,
      null,
      onComplete
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_store?.id]);

  // Filter sales by date, then group products
  const productRows = useMemo(() => {
    if (invalidRange) return [];

    const filteredSales = sales.filter((sale) => {
      const saleDateValue = sale.sellDate;

      if (!saleDateValue) {
        return !fromDate && !toDate;
      }

      const saleDate = new Date(saleDateValue);

      if (Number.isNaN(saleDate.getTime())) {
        return false;
      }

      if (fromDate) {
        const from = new Date(
          `${fromDate}T00:00:00`
        );

        if (saleDate < from) {
          return false;
        }
      }

      if (toDate) {
        const to = new Date(
          `${toDate}T23:59:59.999`
        );

        if (saleDate > to) {
          return false;
        }
      }

      return true;
    });

    const groupedProducts = new Map();

    filteredSales.forEach((sale) => {
      (sale.sellItems || [])
        .filter(
          (item) =>
            Number(item.itemType) === 1 &&
            item.product
        )
        .forEach((item) => {
          const productId = item.product.id;

          if (!groupedProducts.has(productId)) {
            groupedProducts.set(productId, {
              name: item.product.name,
              totalSold: 0,
              totalPurchase: 0,
              totalSales: 0,
              catName:
                item.product.category?.name || "",
            });
          }

          const product = groupedProducts.get(productId);

          const qty = Number(item.qty || 0);

          product.totalSold += qty;

          product.totalPurchase +=
            Number(item.product.purchPrice || 0) * qty;

          product.totalSales +=
            Number(item.product.sellPrice || 0) * qty;
        });
    });

    return Array.from(groupedProducts.values()).map(
      (product) => ({
        name: product.name,
        totalSold: product.totalSold,

        purchPrice: formatCurrency(
          product.totalPurchase,
          _store
        ),

        sellPrice: formatCurrency(
          product.totalSales,
          _store
        ),

        profit: formatCurrency(
          product.totalSales - product.totalPurchase,
          _store
        ),

        catName: product.catName,
      })
    );
  }, [sales, fromDate, toDate, _store, invalidRange]);

  // Shared columns for screen and print
  const columns = [
    {
      id: "name",
      label: t("productName"),
    },
    {
      id: "totalSold",
      label: t("totalSold"),
    },
    {
      id: "purchPrice",
      label: t("purchasePrice"),
    },
    {
      id: "sellPrice",
      label: t("sellPrice"),
    },
    {
      id: "profit",
      label: t("profit"),
    },
  ];

  return (
    <>
      {/* Date filters and Print Preview button */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 3,
        }}
      >
        <Typography variant="h6" sx={{ mr: 1 }}>
          {t("productReport")}
        </Typography>

        <TextField
          label={t("fromDate")}
          type="date"
          size="small"
          value={fromDate}
          onChange={(e) =>
            setFromDate(e.target.value)
          }
          InputLabelProps={{
            shrink: true,
          }}
        />

        <TextField
          label={t("toDate")}
          type="date"
          size="small"
          value={toDate}
          onChange={(e) =>
            setToDate(e.target.value)
          }
          inputProps={{
            min: fromDate || undefined,
          }}
          InputLabelProps={{
            shrink: true,
          }}
          error={invalidRange}
          helperText={
            invalidRange
              ? "To Date must be on or after From Date"
              : ""
          }
        />

        <Button
          variant="outlined"
          onClick={() => {
            setFromDate("");
            setToDate("");
          }}
        >
          {t("clear")}
        </Button>

        <ReportPrintButton
          reportRef={reportRef}
          paperSize="Letter"
          reportTitle={t("productReport")}
        />
      </Box>

      {/* Normal screen report */}
      {/* Search and Pagination remain enabled */}
      <Box
        sx={{
          backgroundColor: "#fff",
          color: "#000",
          p: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            mb: 1,
            fontWeight: "bold",
            textAlign: "center",
          }}
        >
          {t("productReport")}
        </Typography>

        {(fromDate || toDate) && (
          <Typography
            variant="body2"
            sx={{
              mb: 2,
              textAlign: "center",
            }}
          >
            {t("fromDate")}: {fromDate || "All"}
            {"  —  "}
            {t("toDate")}: {toDate || "All"}
          </Typography>
        )}

        <Table
          ActionComp={() => <div />}
          HeaderComp={() => <div />}
          tblHead={columns}
          tblData={productRows}
        />
      </Box>

      {/* Separate printable report - all rows */}
      <Box
        ref={reportRef}
        className="product-report-print"
        sx={{
          position: "absolute",
          left: "-10000px",
          top: 0,
          width: "1000px",
          backgroundColor: "#fff",
          color: "#000",
          p: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            mb: 1,
            fontWeight: "bold",
            textAlign: "center",
          }}
        >
          {t("productReport")}
        </Typography>

        {(fromDate || toDate) && (
          <Typography
            variant="body2"
            sx={{
              mb: 2,
              textAlign: "center",
            }}
          >
            {t("fromDate")}: {fromDate || "All"}
            {"  —  "}
            {t("toDate")}: {toDate || "All"}
          </Typography>
        )}

        {/* All products without Search or Pagination */}
        <Table
          printMode={true}
          ActionComp={() => <div />}
          HeaderComp={() => <div />}
          tblHead={columns}
          tblData={productRows}
        />
      </Box>
    </>
  );
};

const mapStateToProps = (state) => ({
  _store: state.pos.defStore,
});

const mapActionToProps = {
  _getById: actions.getById,
};

export default connect(
  mapStateToProps,
  mapActionToProps
)(ProductReport);
